import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

// The notification data and actions the old NotificationBell owned (HD-4 moved its UI into the shell's
// NotificationsPanel). Polls every 30 s; filter: 'all' | 'scripts' | 'videos'.
const VIDEO_TYPES = ['live_production', 'ai_production', 'video_asset', 'video_comment'];

export function useNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [notifRes, prefRes] = await Promise.all([api.get('/api/notifications'), api.get('/api/notifications/preferences')]);
      setNotifications(notifRes.data);
      setEnabled(prefRes.data.notificationsEnabled);
      setError(false);
    } catch { setError(true); }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const unread = notifications.filter(n => !n.isRead).length;
  const filtered = filter => notifications.filter(n => (filter === 'scripts' ? n.entityType === 'script' : filter === 'videos' ? VIDEO_TYPES.includes(n.entityType) : true));

  const markAllRead = async () => {
    try { await api.patch('/api/notifications/read-all'); setNotifications(p => p.map(n => ({ ...n, isRead: true }))); } catch { /* next poll reconciles */ }
  };
  const markRead = async id => {
    try { await api.patch(`/api/notifications/${id}/read`); setNotifications(p => p.map(n => (n.id === id ? { ...n, isRead: true } : n))); } catch { /* next poll reconciles */ }
  };
  const toggleEnabled = async () => {
    const next = !enabled; setEnabled(next);
    try { await api.patch('/api/notifications/preferences', { notificationsEnabled: next }); } catch { setEnabled(!next); }
  };
  // Same routing the bell used: scripts → Scripts, cards → Production / AI, comments → their review.
  const open = async n => {
    if (!n.isRead) markRead(n.id);
    if (!n.entityType || !n.entityId) return false;
    if (n.entityType === 'script') navigate('/scripts', { state: { openScriptId: n.entityId } });
    else if (n.entityType === 'live_production') navigate(`/production/${n.entityId}`);
    else if (n.entityType === 'ai_production') navigate('/ai', { state: { openRecordId: n.entityId } });
    else if (n.entityType === 'video_comment') {
      try { const res = await api.get(`/api/comments/single/${n.entityId}`); navigate(`/review/${res.data.videoAssetId}?comment=${n.entityId}`); } catch { return false; }
    } else if (n.entityType === 'video_asset') navigate(`/review/${n.entityId}`);
    return true;
  };

  return { notifications, filtered, unread, enabled, loaded, error, refresh, markAllRead, toggleEnabled, open };
}
